import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useClient } from '../../contexts/ClientContext';
import {
  getMonitoringOverview,
  getMonitoringEnvironments,
  getUptimeTrends,
  createMonitoringEnvironment,
  updateMonitoringEnvironment,
  deleteMonitoringEnvironment,
} from '../../services/cloudopsApi';
import MonitoringOverviewCards, { MonitoringOverview } from './components/MonitoringOverviewCards';
import UptimeTrendsChart, { UptimeDataItem } from './components/UptimeTrendsChart';
import EnvironmentsTable, { EnvironmentRow } from './components/EnvironmentsTable';
import CreateEditEnvironmentModal, { Environment, EnvironmentFormData } from './components/CreateEditEnvironmentModal';
import { toast } from 'react-hot-toast';

export default function EnvironmentsPage() {
  const { user } = useAuth();
  const { selectedClientId } = useClient();
  const [loading, setLoading] = useState<boolean>(true);
  const [overview, setOverview] = useState<MonitoringOverview | null>(null);
  const [environments, setEnvironments] = useState<EnvironmentRow[]>([]);
  const [uptimeTrends, setUptimeTrends] = useState<UptimeDataItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState<Environment | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Load all data. When silent=true, skip loading spinner (used for background polling).
  const loadAllData = useCallback(async (silent: boolean = false) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      const clientId = selectedClientId || user?.client_id || undefined;

      const [overviewData, environmentsData, trendsData] = await Promise.all([
        getMonitoringOverview(clientId),
        getMonitoringEnvironments({ clientId }),
        getUptimeTrends({ clientId, days: 30 }),
      ]);

      setOverview(overviewData);
      setEnvironments(environmentsData);
      setUptimeTrends(trendsData);
      if (silent) setError(null);
    } catch (err: unknown) {
      console.error('Error loading monitoring data:', err);
      if (!silent) {
        setError(err instanceof Error ? err.message : 'Failed to load monitoring data');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedClientId, user?.client_id]);

  // Initial load
  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Auto-refresh every 60 seconds (silent — no loading spinner)
  useEffect(() => {
    const interval = setInterval(() => {
      loadAllData(true);
    }, 60000);
    return () => clearInterval(interval);
  }, [loadAllData]);

  const handleCreateEnvironment = () => {
    setSelectedEnvironment(null);
    setIsModalOpen(true);
  };

  const handleEditEnvironment = (environment: EnvironmentRow) => {
    setSelectedEnvironment(environment);
    setIsModalOpen(true);
  };

  const handleSaveEnvironment = async (environmentData: EnvironmentFormData) => {
    try {
      if (selectedEnvironment) {
        await updateMonitoringEnvironment(selectedEnvironment.id!, environmentData);
        toast.success('Environment updated successfully');
      } else {
        await createMonitoringEnvironment(environmentData);
        toast.success('Environment created successfully');
      }
      setIsModalOpen(false);
      loadAllData();
    } catch (err: unknown) {
      console.error('Error saving environment:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to save environment');
    }
  };

  const handleDeleteEnvironment = async (environmentId: string) => {
    if (!globalThis.confirm('Are you sure you want to delete this environment?')) {
      return;
    }

    try {
      await deleteMonitoringEnvironment(environmentId);
      toast.success('Environment deleted successfully');
      loadAllData();
    } catch (err: unknown) {
      console.error('Error deleting environment:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to delete environment');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">
            Error Loading Monitoring Data
          </h3>
          <p className="text-red-700 dark:text-red-300">{error}</p>
          <button
            onClick={() => loadAllData()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Monitoring
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Environment health monitoring and deployment tracking
            </p>
          </div>
          <button
            onClick={handleCreateEnvironment}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Endpoint
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      {overview && <MonitoringOverviewCards overview={overview} />}

      {/* Uptime Trends Chart */}
      {(uptimeTrends?.length ?? 0) > 0 && (
        <div className="mt-8">
          <UptimeTrendsChart data={uptimeTrends} />
        </div>
      )}

      {/* Status Filter Buttons */}
      <div className="mt-8 flex flex-wrap items-center gap-2">
        {['all', 'healthy', 'degraded', 'down', 'unknown'].map((status) => {
          const isActive = statusFilter === status;
          const baseClasses = 'px-4 py-1.5 text-sm font-medium rounded-full border transition-colors';
          const activeClasses: Record<string, string> = {
            all: 'bg-blue-600 text-white border-blue-600',
            healthy: 'bg-green-600 text-white border-green-600',
            degraded: 'bg-yellow-500 text-white border-yellow-500',
            down: 'bg-red-600 text-white border-red-600',
            unknown: 'bg-gray-500 text-white border-gray-500',
          };
          const inactiveClasses = 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700';

          return (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`${baseClasses} ${isActive ? activeClasses[status] : inactiveClasses}`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          );
        })}
      </div>

      {/* Environments Table */}
      <div className="mt-4">
        <EnvironmentsTable
          environments={
            statusFilter === 'all'
              ? environments
              : environments.filter((env) => env.current_status === statusFilter)
          }
          onEdit={handleEditEnvironment}
          onDelete={handleDeleteEnvironment}
        />
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <CreateEditEnvironmentModal
          environment={selectedEnvironment}
          onSave={handleSaveEnvironment}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
}
